import { useEffect, useState } from "react";
import {
  listEmailTemplates, createEmailTemplate, updateEmailTemplate, testSendEmailTemplate,
} from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Plus, Pencil, Check, X, Send, ChevronLeft, Mail, Eye,
} from "lucide-react";

const emptyForm = {
  template_key: "",
  template_name: "",
  subject: "",
  header: "",
  body: "",
  footer: "Regards,\nPrimeS4 Team",
  button_label: "",
  button_url: "",
  active: true,
  notes: "",
};

export default function EmailTemplatesPage() {
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState("list"); // list | edit
  const [editing, setEditing] = useState(null); // id or null for new
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState("");
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState("");

  const load = () => listEmailTemplates().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  // Deep-link support: /admin/email-templates#smartinspect scrolls to a section.
  useEffect(() => {
    if (mode !== "list" || items.length === 0) return;
    const hash = (window.location.hash || "").replace("#", "");
    if (!hash) return;
    const el = document.getElementById(`section-${hash}`);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [items, mode]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setSendResult("");
    setMode("edit");
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      template_key: t.template_key || "",
      template_name: t.template_name || "",
      subject: t.subject || "",
      header: t.header || "",
      body: t.body || "",
      footer: t.footer || "",
      button_label: t.button_label || "",
      button_url: t.button_url || "",
      active: t.active !== false,
      notes: t.notes || "",
    });
    setTestTo("");
    setSendResult("");
    setMode("edit");
  };

  const closeForm = () => { setMode("list"); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    try {
      if (editing) await updateEmailTemplate(editing, form);
      else await createEmailTemplate(form);
      setMsg("Saved");
      await load();
      closeForm();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setSendResult(e?.response?.data?.detail || "Error saving template");
    }
  };

  const sendTest = async () => {
    if (!editing) { setSendResult("Save the template first before sending a test."); return; }
    if (!testTo.trim()) { setSendResult("Enter a recipient email."); return; }
    setSending(true);
    setSendResult("");
    try {
      const res = await testSendEmailTemplate(editing, {
        to: testTo.trim(),
        contact_name: "Alex Example",
        company_name: "Acme Logistics",
        resume_url: "https://example.com/evaluate/resume",
      });
      const status = res?.result?.status;
      const id = res?.result?.email_id;
      if (status === "ok" && id) setSendResult(`Sent — provider id ${id}`);
      else if (status === "stub_or_failed") setSendResult("Logged (no RESEND_API_KEY or send failed — check backend logs).");
      else setSendResult(`Status: ${status || "unknown"}`);
    } catch (e) {
      setSendResult(e?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  if (mode === "edit") return (
    <div data-testid="email-template-edit">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={closeForm} className="text-[#025979] hover:text-[#03738C] text-xs inline-flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to templates
        </button>
      </div>
      <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
        {editing ? "Edit Template" : "New Template"}
      </h1>
      <p className="text-[11px] text-[#6B7280] mt-0.5 mb-5">
        Placeholders like <code className="text-[#025979]">{"{{contact_name}}"}</code>, <code className="text-[#025979]">{"{{company_name}}"}</code>,
        and <code className="text-[#025979]">{"{{resume_url}}"}</code> are replaced at send time.
      </p>

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldText label="Template Key (stable ID)" required testid="tpl-key"
            value={form.template_key}
            onChange={(v) => setForm({ ...form, template_key: v })}
            placeholder="e.g. assessment_submission_confirmation"
            disabled={!!editing}
            hint={editing ? "Template key cannot be changed after creation." : null}
          />
          <FieldText label="Template Name" required testid="tpl-name"
            value={form.template_name}
            onChange={(v) => setForm({ ...form, template_name: v })}
            placeholder="e.g. Assessment Submission Confirmation"
          />
        </div>
        <FieldText label="Subject" required testid="tpl-subject"
          value={form.subject}
          onChange={(v) => setForm({ ...form, subject: v })}
          placeholder="e.g. SmartEvaluate assessment submitted successfully"
        />
        <FieldText label="Header / Title" required testid="tpl-header"
          value={form.header}
          onChange={(v) => setForm({ ...form, header: v })}
          placeholder="Main headline shown at the top of the email"
        />
        <FieldArea label="Body" required testid="tpl-body" rows={10}
          value={form.body}
          onChange={(v) => setForm({ ...form, body: v })}
          placeholder={"Hi {{contact_name}},\n\nThank you for...\n\nYou will receive:\n- Item one\n- Item two"}
          hint='Lines beginning with "- " or "* " render as bullet lists.'
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldText label="Button Label (optional)" testid="tpl-btn-label"
            value={form.button_label}
            onChange={(v) => setForm({ ...form, button_label: v })}
            placeholder="e.g. Resume Assessment"
          />
          <FieldText label="Button URL (optional)" testid="tpl-btn-url"
            value={form.button_url}
            onChange={(v) => setForm({ ...form, button_url: v })}
            placeholder="https://... or {{resume_url}}"
          />
        </div>
        <FieldArea label="Footer / Signature" testid="tpl-footer" rows={3}
          value={form.footer}
          onChange={(v) => setForm({ ...form, footer: v })}
          placeholder="Regards,\nPrimeS4 Team"
        />
        <FieldArea label="Notes (internal)" testid="tpl-notes" rows={2}
          value={form.notes}
          onChange={(v) => setForm({ ...form, notes: v })}
          placeholder="Explain where this template is used, available variables, etc."
        />
        <label className="flex items-center gap-2 text-xs text-[#374151]">
          <input
            data-testid="tpl-active"
            type="checkbox"
            checked={!!form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          Active (available for sending)
        </label>

        <div className="flex items-center gap-2 pt-3 border-t border-[#E5E7EB]">
          <Button data-testid="tpl-save" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
            <Check className="w-3 h-3 mr-1" /> Save Template
          </Button>
          <Button data-testid="tpl-cancel" variant="outline" onClick={closeForm} className="rounded-sm text-xs">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
        </div>
      </div>

      {/* Test send — only for existing templates */}
      {editing && (
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 mt-5" data-testid="tpl-test-send">
          <h3 className="text-sm font-semibold text-[#111827] mb-1">Send Test Email</h3>
          <p className="text-[11px] text-[#6B7280] mb-3">
            Dispatch a live test using this template. Sample values are filled for the placeholders.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="tpl-test-to"
              placeholder="recipient@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className="rounded-sm text-xs flex-1"
            />
            <Button
              data-testid="tpl-test-send-btn"
              onClick={sendTest}
              disabled={sending}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs disabled:opacity-50"
            >
              <Send className="w-3 h-3 mr-1" />{sending ? "Sending..." : "Send Test"}
            </Button>
          </div>
          {sendResult && (
            <p data-testid="tpl-test-result" className="text-[11px] text-[#025979] mt-2">{sendResult}</p>
          )}
        </div>
      )}

      <EmailPreview form={form} />
    </div>
  );

  return (
    <div data-testid="email-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Email Templates</h1>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            One shared template engine powering SmartEvaluate &amp; SmartImprove. Edits take effect immediately for future sends.
          </p>
        </div>
        <Button data-testid="tpl-add-btn" onClick={openAdd} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Template
        </Button>
      </div>

      {msg && (
        <div data-testid="tpl-save-msg" className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]">
          {msg}
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm px-4 py-8 text-center text-xs text-[#9CA3AF]">
          No templates yet. Click <span className="font-semibold text-[#025979]">Add Template</span> to create one.
        </div>
      ) : (
        <div className="space-y-5">
          {groupTemplatesByCategory(items).map((section) => (
            <TemplateSection
              key={section.id}
              section={section}
              onEdit={openEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Visual grouping ----------
// Iter 90: All templates still live in ONE shared `email_templates`
// collection on the backend. The categorisation below is FRONTEND-ONLY
// to help admins find the right template now that SmartEvaluate AND
// SmartImprove flows share the same engine. Adding a new template_key
// auto-routes into the appropriate section based on the prefix; anything
// not matching a known prefix falls into "Shared / Common".
const SMARTEVALUATE_KEYS = new Set([
  "assessment_submission_confirmation",
  "resume_assessment_email",
  "report_share",
  "assessment_contributor_invitation",
]);
const SMARTIMPROVE_KEY_PREFIX = "smartimprove_";
const SMARTINSPECT_KEY_PREFIX = "smartinspect_";

function categoriseTemplate(t) {
  const key = (t.template_key || "").trim();
  if (key.startsWith(SMARTINSPECT_KEY_PREFIX)) return "smartinspect";
  if (key.startsWith(SMARTIMPROVE_KEY_PREFIX)) return "smartimprove";
  if (SMARTEVALUATE_KEYS.has(key)) return "smartevaluate";
  return "shared";
}

function groupTemplatesByCategory(items) {
  const buckets = { shared: [], smartevaluate: [], smartimprove: [], smartinspect: [] };
  for (const t of items) buckets[categoriseTemplate(t)].push(t);
  const sortByName = (arr) =>
    arr.slice().sort((a, b) => (a.template_name || "").localeCompare(b.template_name || ""));
  return [
    {
      id: "shared",
      label: "Shared / Common",
      hint: "Used by both SmartEvaluate and SmartImprove (and platform-wide flows).",
      items: sortByName(buckets.shared),
    },
    {
      id: "smartevaluate",
      label: "SmartEvaluate",
      hint: "Assessment submission, resume, and finalized evaluation report share.",
      items: sortByName(buckets.smartevaluate),
    },
    {
      id: "smartimprove",
      label: "SmartImprove",
      hint: "Engagement submission, resume, and finalized SmartImprove report share.",
      items: sortByName(buckets.smartimprove),
    },
    {
      id: "smartinspect",
      label: "SmartInspect",
      hint: "Executive ROI Business Case email sharing.",
      items: sortByName(buckets.smartinspect),
    },
  ].filter((s) => s.items.length > 0);
}

function TemplateSection({ section, onEdit }) {
  return (
    <section id={`section-${section.id}`} data-testid={`tpl-section-${section.id}`}>
      <div className="flex items-baseline gap-2 mb-2">
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#025979]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {section.label}
        </p>
        <span className="text-[10px] text-[#9CA3AF]">·</span>
        <p className="text-[10px] text-[#6B7280]">{section.hint}</p>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm divide-y divide-[#E5E7EB]">
        {section.items.map((t) => (
          <div
            key={t.id}
            className="px-4 py-3 flex items-start justify-between gap-3"
            data-testid={`tpl-row-${t.template_key}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Mail className="w-3.5 h-3.5 text-[#025979] shrink-0" strokeWidth={1.5} />
                <p className="text-sm font-semibold text-[#111827]">{t.template_name}</p>
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[10px] font-medium ${
                    t.active === false
                      ? "bg-[#F1F5F9] text-[#6B7280] border-[#E5E7EB]"
                      : "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15"
                  }`}
                >
                  {t.active === false ? "Inactive" : "Active"}
                </span>
              </div>
              <p className="text-xs text-[#374151] mt-1">
                <span className="text-[#9CA3AF]">Subject:</span> {t.subject || "—"}
              </p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">key: {t.template_key}</p>
            </div>
            <button
              data-testid={`tpl-edit-${t.template_key}`}
              onClick={() => onEdit(t)}
              className="shrink-0 inline-flex items-center gap-1 text-[#025979] hover:text-[#03738C] text-xs"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Field primitives ----------
function FieldText({ label, value, onChange, placeholder, testid, required, disabled, hint }) {
  return (
    <div>
      <Label className="text-xs font-medium text-[#374151] mb-1 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-sm text-xs"
      />
      {hint && <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p>}
    </div>
  );
}

function FieldArea({ label, value, onChange, placeholder, testid, rows = 4, required, hint }) {
  return (
    <div>
      <Label className="text-xs font-medium text-[#374151] mb-1 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="rounded-sm text-xs font-mono"
      />
      {hint && <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p>}
    </div>
  );
}

// ---------- Preview ----------
function EmailPreview({ form }) {
  const ctx = { contact_name: "Alex Example", company_name: "Acme Logistics", resume_url: "https://example.com/evaluate/resume" };
  const render = (s) => String(s || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => ctx[k] ?? "");
  const bodyHtml = renderBodyHtml(render(form.body));
  return (
    <div className="mt-6" data-testid="tpl-preview">
      <div className="flex items-center gap-2 mb-2">
        <Eye className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
        <p className="text-[11px] font-semibold text-[#025979] uppercase tracking-wider">Preview</p>
      </div>
      <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-4">
        <div className="max-w-[600px] mx-auto bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
          {/* Brand bar — mirrors backend email_service.render_email_html */}
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex justify-between items-center">
            <span
              className="text-[15px] font-bold leading-none"
              style={{ fontFamily: "Montserrat, Arial, Helvetica, sans-serif", letterSpacing: "0.2px" }}
            >
              <span style={{ color: "#0093CB" }}>Prime</span>
              <span style={{ color: "#000000" }}>S4</span>
            </span>
            <span className="text-[10px] text-[#6B7280]">PrimeS4 AI · Operational Intelligence</span>
          </div>
          <div className="p-5">
            <p className="text-[11px] text-[#9CA3AF] mb-1">Subject</p>
            <p className="text-xs text-[#111827] font-medium mb-4">{render(form.subject) || "—"}</p>
            <h2 className="text-lg font-bold text-[#111827] leading-tight mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
              {render(form.header) || "—"}
            </h2>
            <div className="text-[13px] text-[#374151] leading-relaxed" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            {form.button_label && form.button_url && (
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-block mt-3 bg-[#025979] text-white text-xs font-semibold px-4 py-2 rounded-sm"
              >
                {render(form.button_label)}
              </a>
            )}
          </div>
          <div className="px-5 py-3 border-t border-[#E5E7EB] bg-[#F8FAFC]">
            <p className="text-[11px] text-[#6B7280] whitespace-pre-line">{render(form.footer)}</p>
          </div>
        </div>
        {/* Outer copyright strip — mirrors backend wrapper */}
        <p className="max-w-[600px] mx-auto mt-3 text-[10.5px] text-[#9CA3AF] text-center">
          © {new Date().getFullYear()} PrimeS4 · Powered by PRIVA Operational Intelligence
        </p>
      </div>
    </div>
  );
}

function renderBodyHtml(raw) {
  const lines = (raw || "").split("\n");
  const parts = [];
  let para = [], bullets = [];
  const flushPara = () => { if (para.length) { parts.push(`<p style="margin:0 0 10px 0;">${escapeHtml(para.join(" "))}</p>`); para = []; } };
  const flushBullets = () => {
    if (bullets.length) {
      parts.push(`<ul style="margin:0 0 10px 18px;padding:0;">${bullets.map(b => `<li style="margin:0 0 4px 0;">${escapeHtml(b)}</li>`).join("")}</ul>`);
      bullets = [];
    }
  };
  for (const ln of lines) {
    if (!ln.trim()) { flushPara(); flushBullets(); continue; }
    const s = ln.trim();
    if (s.startsWith("- ") || s.startsWith("* ")) { flushPara(); bullets.push(s.slice(2).trim()); }
    else { flushBullets(); para.push(s); }
  }
  flushPara(); flushBullets();
  return parts.join("");
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
